import React from "react";
import Card from "./Card/Card";
import { styled } from "@mui/material/styles";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

const CardWindow = ({
  leadStatusFilter,
  dashPerspective,
  teamLeads,
  handleOpenLeadActionsModal,
  status,
  user,
  memberFullName,
  filtered,
  userId,
  userFullName,
}) => {
  const getEmptyMessage = () => {
    if (status === "awaitingAction") return "No investigations awaiting action";
    if (status === "investigating" || status === "investigation") return "No mortgages under investigation";
    if (status === "awaitingUpdate") return "No mortgages awaiting updates";
    return "No closing investigations";
  };

  const getHeaderTitle = () => {
    if (status === "awaitingAction" && user === "self") {
      return "My Investigations Awaiting Action";
    }
    if (status === "awaitingAction" && user === "team") {
      return "Team Investigations Awaiting Action";
    }
    if (status === "awaitingAction") {
      return memberFullName ? `${memberFullName}'s Investigations Awaiting Action` : `Investigations Awaiting Action`;
    }

    if ((status === "investigating" || status === "investigation") && user === "self") {
      return "My Mortgages Under Investigation";
    }
    if ((status === "investigating" || status === "investigation") && user === "team") {
      return "Team Mortgages Under Investigation";
    }
    if (status === "investigating" || status === "investigation") {
      return memberFullName ? `${memberFullName}'s Mortgages Under Investigation` : `Mortgages Under Investigation`;
    }

    if (status === "awaitingUpdate" && user === "self") {
      return "My Mortgages Awaiting Public Records Update";
    }
    if (status === "awaitingUpdate" && user === "team") {
      return "Team Mortgages Awaiting Public Records Update";
    }
    if (status === "awaitingUpdate") {
      return memberFullName ? `${memberFullName}'s Mortgages Awaiting Public Records Update` : `Mortgages Awaiting Public Records Update`;
    }

    if (status === "closing" && user === "self") {
      return "My Closing Investigations";
    }
    if (status === "closing" && user === "team") {
      return "Team Closing Investigations";
    }

    return memberFullName ? `${memberFullName}'s Closing Investigations` : `Closing Investigations`;
  };

  if (teamLeads.length === 0) {
    return (
      <QueueFrame>
        <ListHeader>
          <HeaderTitleBlock>
            <HeaderEyebrow>Investigation Queue</HeaderEyebrow>
            <HeaderTitle>{getHeaderTitle()}</HeaderTitle>
          </HeaderTitleBlock>

          <HeaderStats>
            {filtered && <FilterBadge>Filtered</FilterBadge>}
            <TotalBadge>
              <span>Total</span>
              <strong>0</strong>
            </TotalBadge>
          </HeaderStats>
        </ListHeader>

        <EmptyStateShell>
          <EmptyIconWrap>
            <VerifiedUserIcon />
          </EmptyIconWrap>
          <EmptyTitle>{getEmptyMessage()}</EmptyTitle>
          <EmptySubtitle>
            {filtered
              ? "No investigations match the current filters."
              : "Records matching this queue will appear here once available."}
          </EmptySubtitle>
        </EmptyStateShell>
      </QueueFrame>
    );
  }

  return (
    <QueueFrame>
      <ListHeader>
        <HeaderTitleBlock>
          <HeaderEyebrow>Investigation Queue</HeaderEyebrow>
          <HeaderTitle>{getHeaderTitle()}</HeaderTitle>
        </HeaderTitleBlock>

        <HeaderStats>
          {filtered && <FilterBadge>Filtered</FilterBadge>}
          <TotalBadge>
            <span>Total</span>
            <strong>{teamLeads.length}</strong>
          </TotalBadge>
        </HeaderStats>
      </ListHeader>

      <CardsScrollArea>
        {teamLeads.map((lead, index) => (
          <Card
            cardIndex={index}
            leadStatusFilter={leadStatusFilter}
            handleOpenLeadActionsModal={handleOpenLeadActionsModal}
            key={lead._id}
            userId={userId}
            userFullName={userFullName}
            dashPerspective={dashPerspective}
            lead={lead}
          />
        ))}
      </CardsScrollArea>
    </QueueFrame>
  );
};

const QueueFrame = styled("section")({
  gridRow: "1 / 3",
  gridColumn: 1,
  minWidth: 0,
  minHeight: 0,
  height: "100%",
  maxHeight: "100%",
  overflow: "hidden",
  display: "grid",
  gridTemplateRows: "4.6rem minmax(0, 1fr)",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: "1rem",
});

const ListHeader = styled("header")(({ theme }) => ({
  gridRow: 1,
  gridColumn: 1,
  minHeight: "4.4rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "1rem",
  padding: "0.7rem 1rem 0.8rem 1rem",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  borderRadius: "0.8rem",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
  boxShadow: "0 0.7rem 1.6rem rgba(15, 23, 42, 0.055)",
}));

const HeaderTitleBlock = styled("div")({
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
});

const HeaderEyebrow = styled("div")(({ theme }) => ({
  color: theme.palette.common.grayText,
  fontSize: "0.9rem",
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
}));

const HeaderTitle = styled("div")({
  color: "#0f172a",
  fontSize: "1.55rem",
  fontWeight: 950,
  letterSpacing: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const HeaderStats = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  flexShrink: 0,
});

const FilterBadge = styled("div")({
  height: "2.5rem",
  display: "inline-flex",
  alignItems: "center",
  padding: "0 0.85rem",
  borderRadius: "999px",
  color: "#1e40af",
  background: "rgba(37,99,235,0.08)",
  border: "0.1rem solid rgba(37,99,235,0.22)",
  fontSize: "0.95rem",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
});

const TotalBadge = styled("div")(({ theme }) => ({
  height: "2.5rem",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0 0.85rem",
  borderRadius: "999px",
  color: "#334155",
  background: "#fff",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,

  "& span": {
    color: theme.palette.common.grayText,
    fontSize: "0.9rem",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },

  "& strong": {
    color: "#0f172a",
    fontSize: "1.15rem",
    fontWeight: 950,
  },
}));

const CardsScrollArea = styled("main")(({ theme }) => ({
  gridRow: 2,
  gridColumn: 1,
  height: "100%",
  maxHeight: "100%",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  padding: "1rem 0.35rem 1rem 0",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: "1rem",
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.common.grayText} ${theme.palette.common.grayBorderLight}`,

  "& > *": {
    flex: "0 0 auto",
    minHeight: "max-content",
  },

  "::-webkit-scrollbar-thumb": {
    borderRadius: "999px",
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
    backgroundColor: theme.palette.common.grayText,
  },

  "::-webkit-scrollbar": {
    width: "0.38rem",
    backgroundColor: theme.palette.common.grayBorderLight,
  },

  "::-webkit-scrollbar-track": {
    borderLeft: `0.1rem solid ${theme.palette.common.grayBorder}`,
    backgroundColor: theme.palette.common.grayBorderLight,
  },
}));

const EmptyStateShell = styled("section")(({ theme }) => ({
  gridColumn: 1,
  gridRow: 2,
  width: "100%",
  height: "100%",
  minHeight: "34rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  margin: "auto",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  borderRadius: "0.8rem",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,250,252,0.88))",
  boxShadow: "inset 0 0 0 0.1rem rgba(255,255,255,0.8)",
}));

const EmptyIconWrap = styled("div")(({ theme }) => ({
  width: "10rem",
  height: "10rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.common.grayBorderLight,
  borderRadius: "999px",
  background: "rgba(15,23,42,0.025)",
  marginBottom: "1.5rem",

  "& svg": {
    width: "6rem",
    height: "6rem",
  },
}));

const EmptyTitle = styled("div")({
  color: "#334155",
  fontSize: "1.8rem",
  fontWeight: 900,
  letterSpacing: 0,
  textTransform: "capitalize",
});

const EmptySubtitle = styled("div")(({ theme }) => ({
  marginTop: "0.5rem",
  color: theme.palette.common.grayText,
  fontSize: "1rem",
  fontWeight: 600,
}));

export default CardWindow;
